import Icon, { IconType } from 'components/Icon';
import { SegmentedButton, SegmentedControl } from 'components/Widget/Button/SegmentedControl';
import DropdownMenu from 'components/Widget/DropdownMenu';
import { Tooltip } from 'components/Widget/Tooltip';
import { Frame, FrameStatus } from 'dragonmouse-shared/interfaces';
import { MenuItemAction } from 'menu/MenuItem';
import { useMemo } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import { selectActiveScene } from 'redux/db/reducers';
import { canEdit, canExportSvg } from 'redux/db/util';
import { alignSelectedFrames, displayViewOnlyModal, FrameSide } from 'redux/mode/util';
import { RootReducer } from 'redux/reducers';
import { useTheme } from 'styled-components';
import Section from '../Section';
import * as Base from '../styles';
import * as S from './styles';

interface MultipleFramePropertiesProps {
	frames: Frame[];
}
const MultipleFrameProperties = ({ frames }: MultipleFramePropertiesProps) => {
	const theme = useTheme();
	const tooltipId = 'header-tooltip';

	const { scene } = useSelector(
		(r: RootReducer) => ({
			scene: selectActiveScene(r.db, r.mode.activeSceneId),
		}),
		shallowEqual
	);

	//------------------------------------------------------------------------------------------------
	// MENU ITEMS
	//------------------------------------------------------------------------------------------------
	const downloadMenuItems = useMemo(() => {
		return [
			MenuItemAction.createDownloadFramesZip(frames, 'png'),
			...(canExportSvg(frames)
				? [MenuItemAction.createDownloadFramesZip(frames, 'svg')]
				: []),
			MenuItemAction.createDownloadFramesZip(frames, 'html'),
		];
	}, [frames]);

	//------------------------------------------------------------------------------------------------
	// RENDERING
	//------------------------------------------------------------------------------------------------

	const iconColor = theme.gray[700];
	const isDisabled = frames.some((frame) => frame.metadata.status !== FrameStatus.READY);

	const align = (direction: FrameSide): void => {
		if (!canEdit(scene)) {
			displayViewOnlyModal();
			return;
		}
		alignSelectedFrames(direction);
	};

	const headerLabel = `${frames.length} Frames`;

	return (
		<Base.Base>
			<Base.Divider />
			<S.HeaderBase>
				<S.HeaderLabel>{headerLabel}</S.HeaderLabel>
				<S.HeaderActionsSection>
					<DropdownMenu menuItems={downloadMenuItems}>
						<S.DropdownTrigger
							data-tooltip-id={tooltipId}
							data-tooltip-content="Download"
							style={{ opacity: isDisabled ? 0.5 : 1, pointerEvents: isDisabled ? 'none' : 'auto' }}
						>
							<Icon
								type={IconType.LINE_ARROW_DOWN}
								width={16}
								height={16}
								color={theme.gray[1000]}
							/>
						</S.DropdownTrigger>
					</DropdownMenu>
					<Tooltip id={tooltipId} placement="top" />
				</S.HeaderActionsSection>
			</S.HeaderBase>
			<Base.ScrollingContainer>
				<Section label="Alignment">
					<Base.Row>
						<SegmentedControl>
							<SegmentedButton
								key="align-left"
								onSelected={() => align('left')}
								tooltip="Align Left"
							>
								<Icon type={IconType.ALIGN_ITEM_LEFT} width={18} height={18} color={iconColor} />
							</SegmentedButton>
							<SegmentedButton
								key="align-right"
								onSelected={() => align('right')}
								tooltip="Align Right"
							>
								<Icon type={IconType.ALIGN_ITEM_RIGHT} width={18} height={18} color={iconColor} />
							</SegmentedButton>
						</SegmentedControl>
						<SegmentedControl>
							<SegmentedButton key="align-top" onSelected={() => align('top')} tooltip="Align Top">
								<Icon type={IconType.ALIGN_ITEM_TOP} width={18} height={18} color={iconColor} />
							</SegmentedButton>
							<SegmentedButton
								key="align-bottom"
								onSelected={() => align('bottom')}
								tooltip="Align Bottom"
							>
								<Icon type={IconType.ALIGN_ITEM_BOTTOM} width={18} height={18} color={iconColor} />
							</SegmentedButton>
						</SegmentedControl>
					</Base.Row>
					<Base.Spacer />
				</Section>
				<Base.Spacer />
			</Base.ScrollingContainer>
		</Base.Base>
	);
};

export default MultipleFrameProperties;
